import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { TranslateModule } from '@ngx-translate/core';
import { TextareaComponent } from '@shared/form/textarea/textarea.component';
import { CardPickerComponent } from '@shared/form/card-picker/card-picker.component';
import { AbstractBaseDialogFormComponent } from '@shared/components/dialog/abstract-base-dialog-form-component.directive';
import { finalize, take } from 'rxjs';
import { IssueReportService } from '@core/services/issue-report/issue-report.service';
import { ConsoleLogger } from '@core/services/issue-report/console-logger.service';
import { IssueSubmitReq } from '@api/model/my-api';
import { Router } from '@angular/router';
import { SpinnerComponent } from '../spinner/spinner.component';
import { LabeledLinkComponent } from '../labeled-data-components/labeled-link/labeled-link.component';
import { StorageService } from '@core/services/storage/storage.service';

@Component({
  selector: 'hd-issue-report-dialog',
  standalone: true,
  imports: [
    DialogComponent,
    TranslateModule,
    TextareaComponent,
    ReactiveFormsModule,
    CardPickerComponent,
    SpinnerComponent,
    LabeledLinkComponent,
  ],
  templateUrl: './issue-report.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IssueReportComponent
  extends AbstractBaseDialogFormComponent
  implements OnInit
{
  private issueService = inject(IssueReportService);
  private storageService = inject(StorageService);
  protected loader$$ = signal<boolean>(false);
  protected submitedIssueLink$$ = signal<string | undefined>(undefined);
  private router = inject(Router);
  private consoleLogger = inject(ConsoleLogger);

  protected form = this.fb.group({
    name: [''],
    descriptionExpected: [
      '',
      [Validators.required, Validators.maxLength(5000)],
    ],
    descriptionActual: ['', [Validators.required, Validators.maxLength(5000)]],
  });

  ngOnInit() {
    this.issueService.captureScreenshot();
    this.issueService.captureUserEmail(this.storageService.getUserEmail());
    this.issueService.captureUserDetails(this.storageService.getUserDetails());
  }

  protected sendData() {
    this.loader$$.set(true);

    const data = this.form.getRawValue();
    const report: IssueSubmitReq = {
      name: data.name,
      userEmail: this.issueService.getUserEmail(),
      userDetails: this.issueService.getUserDetails(),
      descriptionExpected: data.descriptionExpected,
      descriptionActual: data.descriptionActual,
      screenshot: this.issueService.getScreenShot(),
      errors: this.issueService.getErrors(),
      url: window.location.href,
      route: this.router.url,
      timestamp: JSON.stringify(new Date()),
      userAgent: navigator.userAgent,
      consoleLogs: this.consoleLogger.getLogs(),
    };
    console.log('Bug report:', report);

    this.api.issue
      .submitIssueReport(report)
      .pipe(
        take(1),
        finalize(() => {
          this.loader$$.set(false);
        }),
      )
      .subscribe({
        next: (response) => this.submitedIssueLink$$.set(response),
        error: (error) => {
          console.error('Error submitting report:', error);
        },
      });
  }
}
